import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { encounterMemberSharedInputs } from '../compile/encounter-member-input';
import { absorbInventoryMetadata, mergeInventoryMetadata } from '../compile/inventory-shared-catalog';
import type { Row } from './row-plan';
import type { AppliedNewEntity } from '../transport/applied-frame';
import type { BaseEnvelope } from '../compile/base-patch';
import type { SpawnCompileInput } from './preview';
import { readItemField } from '../compile/item-field-reader';
import { compileDefaultAbilityGrants } from '../compile/default-ability-grants';
import { sameJson } from '../compile/json-change';
import { encounterField } from '../../shared/array-types';

/** Birth frames name their raw inputs; each receiver banks only what it lacks. */
export function spawnBirthInputs(source: Row, held: Row, birthRows?: readonly Row[]): Row {
    const catalog = fromBaseRecord(source.catalog) ? source.catalog : {};
    // Discover the required ids without copying their definition closure.
    const templateInputs = fromBaseRecord(source.required_templates) ? source.required_templates : {};
    const enchantments = new Set<string>(Array.isArray(templateInputs.enchantments) ? templateInputs.enchantments.map(String) : []);
    const monsters = new Set<string>(Array.isArray(templateInputs.monsters) ? templateInputs.monsters.map(String) : []);
    const templateCache = new Map<string, Row[]>();
    const templateRows = (key: string): Row[] => {
        let rows = templateCache.get(key);
        if (!rows) { rows = Array.isArray(source[key]) ? source[key].filter(fromBaseRecord) : []; templateCache.set(key, rows); }
        return rows;
    };
    const required = new Set<string>(Array.isArray(source.required_definition_ids) ? source.required_definition_ids.filter((id): id is string => typeof id === 'string') : []);
    const visit = (value: unknown): void => {
        if (typeof value === 'string') {
            for (const id of value.toUpperCase().split('|')) {
                if (!fromBaseUuid(id)) continue;
                if (!enchantments.has(id) && templateRows('enchantment_templates').some(row => readItemField(row, 'enchantment_template_id') === id)) {
                    enchantments.add(id);
                    visit(templateRows('enchantment_template_enchantments').filter(row => readItemField(row, 'enchantment_template_id') === id));
                }
                if (!monsters.has(id) && (templateRows('monster_templates').some(row => readItemField(row, 'monster_template_id') === id)
                    || templateRows('monster_template_layers').some(row => readItemField(row, 'monster_template_id') === id))) {
                    monsters.add(id);
                    visit(templateRows('monster_template_layers').filter(row => readItemField(row, 'monster_template_id') === id));
                }
                if (required.has(id) || !fromBaseRecord(catalog[id])) continue;
                required.add(id); visit(catalog[id]);
            }
        } else if (value && typeof value === 'object') for (const child of Object.values(value)) visit(child);
    };
    if (birthRows) visit(birthRows);
    else for (const key of ['items', 'template', 'item_layer_types', 'encounter_creatures', 'inventory', 'statuses', 'charges',
        'encounter_creatures_spells_prepared']) visit(source[key]);
    if (birthRows || fromBaseRecord(source.encounter_creatures)) visit(compileDefaultAbilityGrants({
        hasEncounterCreatureId: true, itemType: 'creature', legendaryCreature: true }).map(grant => grant.abilityId));
    for (const row of birthRows ?? []) for (const key of ['item_id', 'inventory_item_id', 'status_item_id']) {
        const id = readItemField(row, key);
        if (fromBaseUuid(id)) required.add(id.toUpperCase());
    }
    for (const row of birthRows ?? []) {
        const enchantment = readItemField(row, 'enchantment_template_id');
        const monster = readItemField(row, 'encounter_creature_monster_template_id');
        if (fromBaseUuid(enchantment)) enchantments.add(enchantment.toUpperCase());
        if (fromBaseUuid(monster)) monsters.add(monster.toUpperCase());
    }
    visit(held.encounters); visit(source.encounter_context);
    // Destination references are required even when the deferred Add source has
    // no climate catalog. Use the same reader for lean and compiled room rows.
    for (const context of [held, source.encounter_context].filter(fromBaseRecord))
        for (const key of ['encounter_weather', 'encounter_tempurature', 'encounter_wind']) {
            const id = encounterField<unknown>(context, key);
            if (fromBaseUuid(id)) { required.add(id.toUpperCase()); visit(catalog[id.toUpperCase()]); }
        }
    const memberIds = (templates: Set<string>, key: string, ownerKey: string, idKey: string): Row => !Array.isArray(source[key]) ? {} : Object.fromEntries([...templates].map(id =>
        [id, templateRows(key).filter(row => readItemField(row, ownerKey) === id).map(row => readItemField(row, idKey))]));
    const templates = { enchantments: [...enchantments], monsters: [...monsters],
        enchantment_members: { ...fromBaseRecord(templateInputs.enchantment_members) ? templateInputs.enchantment_members : {},
            ...memberIds(enchantments, 'enchantment_template_enchantments', 'enchantment_template_id', 'enchantment_id') },
        monster_layers: { ...fromBaseRecord(templateInputs.monster_layers) ? templateInputs.monster_layers : {},
            ...memberIds(monsters, 'monster_template_layers', 'monster_template_id', 'monster_template_layer_id') } };
    // A changed codec/epoch is an atomic compiler contract, not a definition
    // closure. Preserve even an invalid value so validation cannot heal it by
    // silently borrowing the receiver's older signature (plcodec31s).
    return { ...(source.pl_signature !== undefined && !sameJson(source.pl_signature, held.pl_signature)
            ? { pl_signature: source.pl_signature } : {}), required_definition_ids: [...required],
        ...(enchantments.size || monsters.size ? { required_templates: templates } : {}) };
}

export function hydrateSpawnBirth(base: Row, held: Row): Row {
    const inputs = encounterMemberSharedInputs(held);
    if (base.spawn_root_definition_missing === true && fromBaseRecord(inputs.catalog)) {
        const id = String(readItemField(base.encounter_creatures as Row, 'item_id') ?? '').toUpperCase();
        const definition = inputs.catalog[id];
        if (fromBaseRecord(definition)) {
            const { items: _items, item_layer_types: _layers, spawn_root_definition_missing: _missing, ...rows } = base;
            base = { ...definition, ...rows };
        }
    }
    if (fromBaseRecord(inputs.catalog) && fromBaseRecord(base.catalog)) {
        const known = inputs.catalog;
        base = { ...base, catalog: Object.fromEntries(Object.entries(base.catalog).map(([id, row]) =>
            [id, fromBaseRecord(known[id]) && sameJson(known[id], row) ? known[id] : row])) };
    }
    return { ...mergeInventoryMetadata(inputs, base),
        encounter_context: base.encounter_context ?? held.encounter_context ?? { encounters: held.encounters, campaign_arr: held.campaign_arr },
        status_owners: Array.isArray(held.encounter_creatures_arr) ? held.encounter_creatures_arr.filter(fromBaseRecord)
            .map(member => member.encounter_creatures).filter(fromBaseRecord) : base.status_owners ?? [] };
}

export function compactSpawnBirth(base: Row, held: Row): Row {
    const shared = encounterMemberSharedInputs(base);
    delete shared.inventory_order;
    return { ...Object.fromEntries(Object.entries(base).filter(([key]) => !(key in shared))), ...spawnBirthInputs(base, held) };
}

/** Resolve only the receiver's missing raw definitions AND their SELECT witnesses.
 * One shared read answers a newborn batch, never a saved-member/encounter read. */
export async function spawnCompileInput(entities: readonly AppliedNewEntity[], context: BaseEnvelope,
    read: (ids: readonly string[], templates: SpawnTemplateNeeds) => Promise<{ base: Row; metadata?: unknown }>): Promise<SpawnCompileInput> {
    let held = context.base, metadata = context.metadata;
    const additions: Row = {}, owned = new WeakSet<object>();
    for (const entity of entities) absorbInventoryMetadata(additions, encounterMemberSharedInputs(entity.base), held, owned);
    if (Object.keys(additions).length) held = mergeInventoryMetadata(held, additions);
    const required = new Set(entities.flatMap(entity => Array.isArray(entity.base.required_definition_ids)
        ? entity.base.required_definition_ids.filter((id): id is string => typeof id === 'string' && fromBaseUuid(id)) : []));
    const sources = (): Row[] => [held, ...entities.map(entity => entity.base)];
    const missingDefinitions = (): string[] => {
        const videos = new Set(sources().flatMap(source => fromBaseRecord(source.definition_videos)
            && Array.isArray(source.definition_videos.lookupIds) ? source.definition_videos.lookupIds.map(String) : []));
        return [...required].filter(id => !videos.has(id)
            || !sources().some(source => fromBaseRecord(source.catalog) && fromBaseRecord(source.catalog[id])));
    };
    const requirements = entities.map(entity => entity.base.required_templates).filter(fromBaseRecord);
    const missingTemplates = (mode: 'enchantments' | 'monsters', rows: string, key: string, members: string, memberRows: string, memberKey: string): string[] => {
        const ids = new Set(requirements.flatMap(required => Array.isArray(required[mode]) ? required[mode].map(String) : []));
        const selected = sources().flatMap(source => Array.isArray(source[rows]) ? source[rows].filter(fromBaseRecord) : []);
        const selectedMembers = sources().flatMap(source => Array.isArray(source[memberRows]) ? source[memberRows].filter(fromBaseRecord) : []);
        return [...ids].filter(id => !selected.some(row => readItemField(row, key) === id)
            || !sources().some(source => Array.isArray(source[memberRows]))
            || requirements.some(required => fromBaseRecord(required[members]) && Array.isArray(required[members][id])
                && required[members][id].some(ref => !selectedMembers.some(row => readItemField(row, key) === id && readItemField(row, memberKey) === ref))));
    };
    const neededTemplates = (): SpawnTemplateNeeds => ({ enchantment_template_ids: missingTemplates('enchantments', 'enchantment_templates', 'enchantment_template_id',
        'enchantment_members', 'enchantment_template_enchantments', 'enchantment_id'),
        monster_template_ids: missingTemplates('monsters', 'monster_templates', 'monster_template_id', 'monster_layers', 'monster_template_layers', 'monster_template_layer_id') });
    const missing = missingDefinitions(), templates = neededTemplates();
    if (missing.length || templates.enchantment_template_ids.length || templates.monster_template_ids.length || !fromBaseRecord(metadata)) {
        try {
            let timer: ReturnType<typeof setTimeout> | undefined;
            const selected = await Promise.race([read(missing, templates), new Promise<never>((_resolve, reject) => {
                timer = setTimeout(() => reject(new Error('spawn:input-banking-timeout')), 20000 * spawnInputReadChunks(missing, templates));
            })]).finally(() => clearTimeout(timer));
            held = mergeInventoryMetadata(held, selected.base);
            metadata ??= selected.metadata;
        } catch (error) {
            const names = entities.map(entity => `${readItemField(entity.base.encounter_creatures as Row, 'encounter_creature_name') || 'Creature'}:${entity.entity_id}`);
            throw new Error(`spawn:creature:${names.join(',')}:${error instanceof Error ? error.message : 'definition-read'}:inputs:${[...missing, ...templates.enchantment_template_ids, ...templates.monster_template_ids].join(',')}`);
        }
    }
    const unavailable = missingDefinitions(), unavailableTemplates = neededTemplates();
    unavailable.push(...unavailableTemplates.enchantment_template_ids.map(id => `enchantment-template:${id}`),
        ...unavailableTemplates.monster_template_ids.map(id => `monster-template:${id}`));
    if (unavailable.length) throw new Error(`spawn:creature:${entities.map(entity =>
        `${readItemField(entity.base.encounter_creatures as Row, 'encounter_creature_name') || 'Creature'}:${entity.entity_id}`).join(',')}:inputs-unavailable:${unavailable.join(',')}`);
    if (!fromBaseRecord(metadata)) throw new Error(`spawn:frontend-metadata-unavailable:${entities.map(entity =>
        readItemField(entity.base.encounter_creatures as Row, 'encounter_creature_name') || entity.entity_id).join(',')}`);
    return { entities: entities.map(entity => ({ ...entity, base: hydrateSpawnBirth(entity.base, held) })),
        metadata: metadata as SpawnCompileInput['metadata'] };
}

export interface SpawnTemplateNeeds { enchantment_template_ids: readonly string[]; monster_template_ids: readonly string[] }

/** The SELECT lane permits 128 definitions/enchantments or 32 monster templates per chunk. */
export function spawnInputReadChunks(ids: readonly string[], templates: SpawnTemplateNeeds): number {
    return Math.ceil(Math.max(1, ids.length, templates.enchantment_template_ids.length, templates.monster_template_ids.length * 4) / 128);
}
